import { DateTime } from "luxon"

import { DateTimeFactory } from "../../../../../localization/useDateTime"
import { MeetingTimeLeftVariant } from "../../../../../../shared/components/meetingTimeLeftTag/MeetingTimeLeftTag"

import {
    AVAILABLE_STATUSES,
    DeliveryType,
    FilteredStatuses,
    MeetingServiceOrder,
    MeetingServiceOrderStatus,
} from "../meetingServiceTypes"
import { getDateKey, getTimeSlotLabel, resolveDeliveryDateTime } from "../components/deliveryListModal/helpers"

export enum ProductionGroupBy {
    TIME = "time",
    STATUS = "status",
    LOCATION = "location",
    DATE = "date",
}

/** An order counts as delivered ("done") once its delivery status reaches DELIVERED (or BOOKED). */
export function isDeliveredStatus(status: FilteredStatuses): boolean {
    return status === MeetingServiceOrderStatus.DELIVERED || status === MeetingServiceOrderStatus.BOOKED
}

/**
 * Whether the meeting is happening right now (same window logic as the home upcoming-orders timing). Orders
 * without a valid meeting start/end (e.g. `0000-00-00`) are never "in progress".
 */
export function isMeetingInProgress(
    order: MeetingServiceOrder,
    dateTimeFactory: DateTimeFactory,
    now: DateTime,
): boolean {
    const startStr = order.meetingDetails.startDateTime || order.deliveryDetails.time || ""
    const start = startStr ? dateTimeFactory.fromApi(startStr) : null
    const end = order.meetingDetails.endDateTime ? dateTimeFactory.fromApi(order.meetingDetails.endDateTime) : null
    return Boolean(start?.isValid && end?.isValid && now >= start && now < end)
}

export type TimeToStart = Readonly<{
    minutes: number
    variant: MeetingTimeLeftVariant
}>

/**
 * Minutes until the meeting starts, bucketed into the header countdown tag's colour: 30-45 min green,
 * 10-29 min yellow, under 10 min red. Returns undefined outside that window (started, or more than 45 min away).
 */
export function getTimeToStart(
    order: MeetingServiceOrder,
    dateTimeFactory: DateTimeFactory,
    now: DateTime,
): TimeToStart | undefined {
    const startStr = order.meetingDetails.startDateTime || order.deliveryDetails.time || ""
    const start = startStr ? dateTimeFactory.fromApi(startStr) : null
    if (!start?.isValid) return undefined

    const minutes = Math.ceil(start.diff(now).as("minutes"))
    if (minutes < 0 || minutes > 45) return undefined

    if (minutes >= 30) return { minutes, variant: MeetingTimeLeftVariant.GREEN }
    if (minutes >= 10) return { minutes, variant: MeetingTimeLeftVariant.YELLOW }
    return { minutes, variant: MeetingTimeLeftVariant.RED }
}

const MAX_SORT = Number.MAX_SAFE_INTEGER
const UNKNOWN_KEY = "unknown"

export type ProductionStatusGroup = Readonly<{
    status: FilteredStatuses
    orders: ReadonlyArray<MeetingServiceOrder>
}>

/** One hourly time slot (08:00 - 09:00 …) — same slots and label as the delivery list. */
export type ProductionHourGroup = Readonly<{
    key: string
    timeLabel: string
    orders: ReadonlyArray<MeetingServiceOrder>
}>

/** Orders for one delivery date split into hourly slots — the delivery list's "group by time" layout. */
export type ProductionDateSection = Readonly<{
    dateKey: string
    date: DateTime | undefined
    hourGroups: ReadonlyArray<ProductionHourGroup>
}>

/** Sentinel location key for deliveries without a resolvable location name. */
export const LOCATION_UNKNOWN = "__unknown__"

/** Query param that opens the production view directly in fullscreen mode, e.g. for screen previews. */
export const PRODUCTION_VIEW_FULLSCREEN_PARAM = "fullscreen"

export type ProductionLocationGroup = Readonly<{
    // A DeliveryType value (canteen/pick-up — translated via enum), a real location name, or LOCATION_UNKNOWN.
    key: string
    orders: ReadonlyArray<MeetingServiceOrder>
}>

/** Locations for one delivery date — the delivery list's "group by location" layout (date → location). */
export type ProductionLocationSection = Readonly<{
    dateKey: string
    date: DateTime | undefined
    locationGroups: ReadonlyArray<ProductionLocationGroup>
}>

/** One delivery-date group (no hourly split) — used by the "Not approved" view so the delivery date is visible. */
export type ProductionSimpleDateGroup = Readonly<{
    key: string
    dateLabel: string
    orders: ReadonlyArray<MeetingServiceOrder>
}>

/** What the production board renders, discriminated by the active grouping mode. */
export type ProductionLayout =
    | Readonly<{ mode: ProductionGroupBy.TIME; sections: ProductionDateSection[] }>
    | Readonly<{ mode: ProductionGroupBy.LOCATION; locationSections: ProductionLocationSection[] }>
    | Readonly<{ mode: ProductionGroupBy.STATUS; statusGroups: ReadonlyArray<ProductionStatusGroup> }>
    | Readonly<{ mode: ProductionGroupBy.DATE; dateGroups: ProductionSimpleDateGroup[] }>

// Delivery date/time, resolved the same way as the delivery list (first delivery time, else the order time).
function resolveDeliveryDate(order: MeetingServiceOrder, dateTimeFactory: DateTimeFactory): DateTime | undefined {
    const iso = resolveDeliveryDateTime(order)
    if (!iso) return undefined
    const dt = dateTimeFactory.fromISO(iso)
    return dt.isValid ? dt : undefined
}

function orderMillis(order: MeetingServiceOrder, dateTimeFactory: DateTimeFactory): number {
    return resolveDeliveryDate(order, dateTimeFactory)?.toMillis() ?? MAX_SORT
}

function slotMillis(slotKey: string, dateTimeFactory: DateTimeFactory): number {
    const dt = dateTimeFactory.fromISO(slotKey)
    return dt.isValid ? dt.toMillis() : MAX_SORT
}

export function groupOrdersByDateAndHour(
    orders: ReadonlyArray<MeetingServiceOrder>,
    dateTimeFactory: DateTimeFactory,
): ProductionDateSection[] {
    const sections = new Map<string, { date: DateTime | undefined; slots: Map<string, MeetingServiceOrder[]> }>()

    for (const order of orders) {
        const dt = resolveDeliveryDate(order, dateTimeFactory)
        const dateKey = dt ? getDateKey(dt) : UNKNOWN_KEY
        const slotKey = dt ? dt.startOf("hour").toISO() ?? UNKNOWN_KEY : UNKNOWN_KEY

        let section = sections.get(dateKey)
        if (!section) {
            section = { date: dt?.startOf("day"), slots: new Map() }
            sections.set(dateKey, section)
        }
        const slot = section.slots.get(slotKey)
        if (slot) {
            slot.push(order)
        } else {
            section.slots.set(slotKey, [order])
        }
    }

    return Array.from(sections.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, { date, slots }]) => ({
            dateKey,
            date,
            hourGroups: Array.from(slots.entries())
                .sort(([a], [b]) => slotMillis(a, dateTimeFactory) - slotMillis(b, dateTimeFactory))
                .map(([slotKey, slotOrders]) => ({
                    key: slotKey,
                    timeLabel: slotKey === UNKNOWN_KEY ? "—" : getTimeSlotLabel(slotKey, dateTimeFactory),
                    orders: slotOrders,
                })),
        }))
}

/**
 * Orders grouped by delivery date only (no hourly split), sorted by date then delivery time. Used by the
 * "Not approved" view, whose orders span many dates, so the delivery date is shown in each group header.
 */
export function groupOrdersByDate(
    orders: ReadonlyArray<MeetingServiceOrder>,
    dateTimeFactory: DateTimeFactory,
): ProductionSimpleDateGroup[] {
    const groups = new Map<string, { date: DateTime | undefined; orders: MeetingServiceOrder[] }>()

    for (const order of orders) {
        const dt = resolveDeliveryDate(order, dateTimeFactory)
        const dateKey = dt ? getDateKey(dt) : UNKNOWN_KEY

        const group = groups.get(dateKey)
        if (group) {
            group.orders.push(order)
        } else {
            groups.set(dateKey, { date: dt?.startOf("day"), orders: [order] })
        }
    }

    return Array.from(groups.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, { date, orders: dateOrders }]) => ({
            key: dateKey,
            dateLabel: date ? dateTimeFactory.formatDayAndDate(date) : "—",
            orders: [...dateOrders].sort((a, b) => orderMillis(a, dateTimeFactory) - orderMillis(b, dateTimeFactory)),
        }))
}

/**
 * Orders grouped by their delivery status, ordered by the canonical status order (`AVAILABLE_STATUSES`),
 * with any unlisted statuses appended so no order is dropped.
 */
export function groupOrdersByStatus(
    orders: ReadonlyArray<MeetingServiceOrder>,
): ReadonlyArray<readonly [FilteredStatuses, ReadonlyArray<MeetingServiceOrder>]> {
    const groups = new Map<FilteredStatuses, MeetingServiceOrder[]>()

    for (const order of orders) {
        const status = order.deliveryDetails.statusDetails.status
        const bucket = groups.get(status)
        if (bucket) {
            bucket.push(order)
        } else {
            groups.set(status, [order])
        }
    }

    const ordered = AVAILABLE_STATUSES.filter((status) => groups.has(status))
    const extra = Array.from(groups.keys()).filter((status) => !AVAILABLE_STATUSES.includes(status))

    return [...ordered, ...extra].map((status) => [status, groups.get(status)!] as const)
}

// Canteen/pick-up have no real location, so the delivery type itself is the location (translated via enum).
function resolveLocationKey(order: MeetingServiceOrder): string {
    const type = order.deliveryDetails.type
    if (type === DeliveryType.EATING_IN_CANTEEN || type === DeliveryType.PICK_UP) return type
    return order.deliveryDetails.location?.name || LOCATION_UNKNOWN
}

/**
 * Orders grouped like the delivery list's "group by location": date → location, with each location's
 * orders sorted by delivery time.
 */
export function groupOrdersByLocation(
    orders: ReadonlyArray<MeetingServiceOrder>,
    dateTimeFactory: DateTimeFactory,
): ProductionLocationSection[] {
    const sections = new Map<string, { date: DateTime | undefined; locations: Map<string, MeetingServiceOrder[]> }>()

    for (const order of orders) {
        const dt = resolveDeliveryDate(order, dateTimeFactory)
        const dateKey = dt ? getDateKey(dt) : UNKNOWN_KEY
        const locationKey = resolveLocationKey(order)

        let section = sections.get(dateKey)
        if (!section) {
            section = { date: dt?.startOf("day"), locations: new Map() }
            sections.set(dateKey, section)
        }
        const location = section.locations.get(locationKey)
        if (location) {
            location.push(order)
        } else {
            section.locations.set(locationKey, [order])
        }
    }

    return Array.from(sections.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, { date, locations }]) => ({
            dateKey,
            date,
            locationGroups: Array.from(locations.entries())
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, locationOrders]) => ({
                    key,
                    orders: [...locationOrders].sort(
                        (a, b) => orderMillis(a, dateTimeFactory) - orderMillis(b, dateTimeFactory),
                    ),
                })),
        }))
}
